/**
 * Screenshot Capture Utility for Aura Projects
 *
 * Captures screenshots from iframe preview content using html-to-image
 * and returns base64 data for upload to CDN
 */
import { toPng } from "html-to-image"

export interface CaptureOptions {
  width?: number
  height?: number
  quality?: number
}

const DEFAULT_OPTIONS: CaptureOptions = {
  width: 800,
  height: 600,
  quality: 0.8,
}

/**
 * Capture a screenshot from HTML content by rendering it in a temporary container
 * Returns base64 PNG data (without data: prefix)
 */
async function captureScreenshotFromHtml(html: string, options: CaptureOptions = {}): Promise<string | null> {
  const { width, height } = { ...DEFAULT_OPTIONS, ...options }

  try {
    // Create a temporary container
    const container = document.createElement("div")
    container.style.position = "fixed"
    container.style.left = "-9999px"
    container.style.top = "0"
    container.style.width = `${width}px`
    container.style.height = `${height}px`
    container.style.overflow = "hidden"
    container.style.backgroundColor = "#ffffff"

    // Create iframe to render the HTML
    const iframe = document.createElement("iframe")
    iframe.style.width = `${width}px`
    iframe.style.height = `${height}px`
    iframe.style.border = "none"
    iframe.style.transform = "scale(1)"
    iframe.style.transformOrigin = "top left"

    container.appendChild(iframe)
    document.body.appendChild(container)

    // Wait for iframe to be ready
    await new Promise<void>((resolve) => {
      iframe.onload = () => resolve()
      iframe.srcdoc = html
    })

    // Give the iframe content time to render
    await new Promise((resolve) => setTimeout(resolve, 500))

    // Capture the container
    const dataUrl = await toPng(container, {
      width,
      height,
      quality: options.quality ?? DEFAULT_OPTIONS.quality,
      cacheBust: true,
      pixelRatio: 1,
    })

    // Clean up
    document.body.removeChild(container)

    // Return base64 without the data URL prefix
    return dataUrl.replace(/^data:image\/png;base64,/, "")
  } catch (error) {
    console.error("[captureScreenshotFromHtml] Failed to capture screenshot:", error)
    return null
  }
}

/**
 * Capture a screenshot directly from an existing iframe element
 * This is more reliable but requires the iframe to be visible
 */
async function captureScreenshotFromIframe(
  iframe: HTMLIFrameElement,
  options: CaptureOptions = {},
): Promise<string | null> {
  const { width, height } = { ...DEFAULT_OPTIONS, ...options }

  try {
    // Get the iframe's document
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
    if (!iframeDoc) {
      console.error("[captureScreenshotFromIframe] Cannot access iframe document")
      return null
    }

    // Create a temporary container that wraps the iframe content
    const container = document.createElement("div")
    container.style.position = "fixed"
    container.style.left = "-9999px"
    container.style.top = "0"
    container.style.width = `${width}px`
    container.style.height = `${height}px`
    container.style.overflow = "hidden"
    container.style.backgroundColor = "#ffffff"

    // Clone the iframe's body content
    const clonedBody = iframeDoc.body.cloneNode(true) as HTMLElement
    clonedBody.style.margin = "0"
    clonedBody.style.padding = "0"
    clonedBody.style.width = `${width}px`
    clonedBody.style.height = `${height}px`
    clonedBody.style.overflow = "hidden"
    clonedBody.style.transform = "scale(0.5)"
    clonedBody.style.transformOrigin = "top left"

    // Copy styles from iframe
    const styles = iframeDoc.head.querySelectorAll("style, link[rel='stylesheet']")
    const styleContainer = document.createElement("div")
    styles.forEach((style) => {
      styleContainer.appendChild(style.cloneNode(true))
    })

    container.appendChild(styleContainer)
    container.appendChild(clonedBody)
    document.body.appendChild(container)

    // Give time for styles to apply
    await new Promise((resolve) => setTimeout(resolve, 100))

    // Capture
    const dataUrl = await toPng(container, {
      width,
      height,
      quality: options.quality ?? DEFAULT_OPTIONS.quality,
      cacheBust: true,
      pixelRatio: 1,
    })

    // Clean up
    document.body.removeChild(container)

    return dataUrl.replace(/^data:image\/png;base64,/, "")
  } catch (error) {
    console.error("[captureScreenshotFromIframe] Failed to capture screenshot:", error)
    return null
  }
}

/**
 * Simpler approach: capture using canvas from iframe
 * Most reliable method that works with cross-origin content
 */
export async function captureScreenshotSimple(html: string, options: CaptureOptions = {}): Promise<string | null> {
  const { width = 800, height = 600 } = options

  return new Promise((resolve) => {
    try {
      // Create hidden iframe
      const iframe = document.createElement("iframe")
      iframe.style.position = "fixed"
      iframe.style.left = "-9999px"
      iframe.style.top = "0"
      iframe.style.width = `${width * 2}px` // Render at 2x for quality
      iframe.style.height = `${height * 2}px`
      iframe.style.border = "none"
      iframe.style.backgroundColor = "#ffffff"

      document.body.appendChild(iframe)

      iframe.onload = async () => {
        // Wait for content to render
        await new Promise((r) => setTimeout(r, 800))

        try {
          // Use html-to-image on the iframe's body
          const iframeDoc = iframe.contentDocument
          if (!iframeDoc?.body) {
            document.body.removeChild(iframe)
            resolve(null)
            return
          }

          const dataUrl = await toPng(iframeDoc.body, {
            width: width * 2,
            height: height * 2,
            quality: 0.9,
            cacheBust: true,
            pixelRatio: 0.5, // Scale down to target size
          })

          document.body.removeChild(iframe)
          resolve(dataUrl.replace(/^data:image\/png;base64,/, ""))
        } catch (err) {
          console.error("[captureScreenshotSimple] Capture failed:", err)
          document.body.removeChild(iframe)
          resolve(null)
        }
      }

      iframe.onerror = () => {
        document.body.removeChild(iframe)
        resolve(null)
      }

      iframe.srcdoc = html
    } catch (error) {
      console.error("[captureScreenshotSimple] Setup failed:", error)
      resolve(null)
    }
  })
}
